import { Hotel, MapPin, Calendar, Moon, Coffee, Building2 } from 'lucide-react';
import { InfoItem } from './TransferVoucherSections';

const HotelSection = ({ data, config, t, formatDate, getServiceIcon }) => (
  <div className='pdf-section' style={{
    background: 'transparent',
    marginBottom: config.gap
  }}>
    <div style={{ 
      marginBottom: 0,
      fontFamily: config.fontFamily,
      background: config.sectionColor,
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: config.sectionTitle?.fontWeight || 'bold',
        color: config.sectionTitle?.color || config.primaryColor,
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.sectionTitle?.borderColor || config.primaryColor}`,
        textTransform: config.sectionTitle?.textTransform || 'uppercase',
        letterSpacing: config.sectionTitle?.letterSpacing || '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        {getServiceIcon ? getServiceIcon('hotel') : <Hotel size={20} />}
        {data.name || t('hotelInformation')}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
          gap: 20,
          marginBottom: 24
        }}>
          <InfoItem
            config={config}
            label={t('hotelName')}
            value={data.product.name || t('notAvailable')}
            icon={<Building2 size={config.iconSize || 16} />}
          />

          <InfoItem
            config={config}
            label={t('address')}
            value={data.product.address || t('notAvailable')}
            icon={<MapPin size={config.iconSize || 16} />}
          />

          <InfoItem
            config={config}
            label={t('stayPeriod')}
            value={`${formatDate(data.checkin)} → ${formatDate(data.checkout)}`}
            icon={<Calendar size={config.iconSize || 16} />}
          />

            <InfoItem
              config={config}
              label={t('nights')}
              value={`${data.booking_hotel.nights} ${t('nights')}`}
              icon={<Moon size={config.iconSize || 16} />}
            />

          {data.booking_hotel?.meal_name && (
            <InfoItem
              config={config}
              label={t('mealPlan')}
              value={data.booking_hotel.meal_name}
              icon={<Coffee size={config.iconSize || 16} />}
            />
          )}
        </div>
      </div>

      {/* Détails d'occupation - Même structure que les passagers du flight */}
      {(data.booking_hotel?.adult_count !== undefined || data.booking_hotel?.child_count !== undefined || data.booking_hotel?.rooms_count !== undefined) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginBottom: 16
        }}>
          <div style={{
            fontSize: config.fontSize* 0.9 || 14,
            color: config.primaryColor,
            fontWeight: config.infoLabelWeight || 500
          }}>
            {t('occupancyDetails')}
          </div>
          <div style={{
            fontSize: config.fontSize || 16,
            color: config.textColor || '#1f2937',
            fontWeight: config.infoValueWeight || 600,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}>
            {data.booking_hotel.adult_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {t('adults')}: {data.booking_hotel.adult_count}
              </span>
            )}
            {data.booking_hotel.child_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ margin: '0 4px' }}>•</span>
                {t('children')}: {data.booking_hotel.child_count}
              </span>
            )}
            {data.booking_hotel.rooms_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ margin: '0 4px' }}>•</span>
                {t('rooms')}: {data.booking_hotel.rooms_count}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Détails des chambres - Même structure que les itinéraires du flight */}
      {data.booking_hotel?.rooms && data.booking_hotel.rooms.length > 0 && (
        <div>
          {/* Titre des chambres */}
          {config.showRoomsTitle !== false && (
            <div style={{
              fontSize: config.fontSize || 16,
              fontWeight: config.subsectionTitleWeight || 600,
              color: config.secondaryColor,
              marginBottom: 12
            }}>
              {t('roomDetails')}
            </div>
          )}

          {data.booking_hotel.rooms.map((room, index) => (
            <div key={index} style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              background: config.backgroundColor,
              borderRadius: '8px',
              border: `1px solid ${config.sectionColor}40`
            }}>
              {/* Header de la chambre - Même que header de segment */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: `1px solid ${config.sectionColor}40`
              }}>
                <div>
                  <div style={{ 
                    fontSize: config.fontSize*0.9 || 16, 
                    fontWeight: config.itineraryHeaderWeight || 600, 
                    color: config.textColor || '#1f2937'
                  }}>
                    {t('room')} {index + 1}: {room.name || `${t('roomType')} ${index + 1}`}
                  </div>
                  {room.bed_type && (
                    <div style={{ 
                      fontSize: config.fontSize || 14, 
                      color: config.secondaryColor
                    }}>
                      {room.bed_type}
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontSize: config.fontSize * 0.9 || 14, 
                  fontWeight: '600', 
                  color: config.primaryColor,
                  background: config.sectionColor,
                  padding: '4px 12px',
                  borderRadius: '20px'
                }}>
                  {room.count || 1} {room.count > 1 ? t('rooms') : t('room')}
                </div>
              </div>

              {/* Détails de la chambre - Même grille que flight */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: config.gridGap || 20
              }}>
                {/* Occupants */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }}>
                  <div style={{
                    fontSize: config.fontSize*0.9 || 14,
                    color: config.primaryColor,
                    fontWeight: config.infoLabelWeight || 500
                  }}>
                    {t('occupants')}
                  </div>
                  <div style={{
                    fontSize: config.fontSize || 16,
                    color: config.textColor || '#1f2937',
                    fontWeight: config.infoValueWeight || 600
                  }}>
                    {room.adult || 0} {t('adults')}
                    {room.child > 0 && ` + ${room.child} ${t('children')}`}
                  </div>
                </div>

                {/* Prix */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }}>
                  <div style={{
                    fontSize: config.fontSize*0.9 || 14,
                    color: config.primaryColor,
                    fontWeight: config.infoLabelWeight || 500
                  }}>
                    {t('price')}
                  </div>
                  <div style={{
                    fontSize: config.fontSize || 16,
                    color: config.textColor || '#1f2937',
                    fontWeight: config.infoValueWeight || 600
                  }}>
                    {room.amount?.toLocaleString()} {data.currency}
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Informations complémentaires */}
      {data.policies?.length > 0 && (
        <div style={{ 
          marginTop: '24px',
          padding: '16px',
          background: `${config.backgroundColor}40`,
          borderRadius: '8px',
          border: `1px solid ${config.backgroundColor}`
        }}>
          <div style={{ 
            fontSize: config.fontSize, 
            fontWeight: '600', 
            color: config.textColor || '#1f2937',
            marginBottom: '12px'
          }}>
            {t('additionalInformation')}
          </div>

          {/* Commentaires sur le tarif */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: config.fontSize, fontWeight: '600', color: config.textColor || '#374151', marginBottom: '8px' }}>
              {t('rateComments')}
            </div>
            {data.policies.map((policy, index) => (
              <div key={index} style={{
                fontSize: config.fontSize,
                color: config.textColor || '#4b5563',
                marginBottom: '6px',
                lineHeight: '1.4',
                paddingLeft: '12px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  color: config.secondaryColor
                }}>
                  •
                </span>
                {policy.value}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Mettre à jour le mapping SectionComponents pour inclure le composant hotel
export const HotelSectionComponents = {
  HotelSection,
};

// Fonction utilitaire pour obtenir un composant par nom
export const getHotelSectionComponent = (componentName) => {
  return HotelSectionComponents[componentName] || null;
};

export default HotelSection;